const CAMERA_ACCESS_ERROR_NAMES = new Set([
  'NotFoundError',
  'NotAllowedError',
  'PermissionDeniedError',
  'NotReadableError',
  'OverconstrainedError',
  'ConstraintNotSatisfiedError',
  'AbortError',
  'SecurityError',
])

export type CameraEnvironmentIssue = 'insecure-context' | 'api-unavailable'

/** Detect browser conditions that prevent RTC from opening the camera. */
export function getCameraEnvironmentIssue(): CameraEnvironmentIssue | null {
  if (typeof window === 'undefined') {
    return 'api-unavailable'
  }
  if (!window.isSecureContext) {
    return 'insecure-context'
  }
  if (typeof navigator?.mediaDevices !== 'object') {
    return 'api-unavailable'
  }
  return null
}

export type CameraAccessErrorKind =
  | 'not-found'
  | 'not-allowed'
  | 'permission-denied'
  | 'in-use'
  | 'overconstrained'
  | 'unsupported'
  | 'capture-unsupported'
  | 'generic'

/** True for camera failures that should prompt the user to open or allow the device camera. */
export function isCameraAccessError(error: unknown): boolean {
  return classifyCameraAccessError(error) !== null
}

/** Map browser or RTC camera failures to a stable category for user-facing copy. */
export function classifyCameraAccessError(error: unknown): CameraAccessErrorKind | null {
  if (error instanceof DOMException) {
    switch (error.name) {
      case 'NotFoundError':
        return 'not-found'
      case 'NotAllowedError':
        return 'not-allowed'
      case 'PermissionDeniedError':
        return 'permission-denied'
      case 'NotReadableError':
        return 'in-use'
      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError':
        return 'overconstrained'
      case 'TypeError':
      case 'AbortError':
      case 'SecurityError':
        return 'generic'
      default:
        return CAMERA_ACCESS_ERROR_NAMES.has(error.name) ? 'generic' : null
    }
  }

  if (error instanceof Error) {
    if (error.message.includes('Camera is not supported')) {
      return 'unsupported'
    }
    if (error.message.includes('captureStream is not supported')) {
      return 'capture-unsupported'
    }
    if (error.message.includes('No active video track')) {
      return 'in-use'
    }
  }

  return null
}
